// Pure parser for path-based Update Feed routes. Kept out of the imperative
// router so it can be unit-tested directly (mirrors parseGroupRoute).

import { PATH_CODE_MAP } from './config.js';

/**
 * Parse a path-based scoped Update Feed route.
 *
 *   /groups/{id}/updates            → { params: { group: id } }         (a Group's feed)
 *   /{shortcode}.{id}/updates       → { params: { binder } }            (a binder's feed)
 *   /{shortcode}.{id}/updates/{seq} → { params: { binder, seq } }       (one Update's detail)
 *
 * An Update always belongs to a binder (never a Group), so its detail nests
 * under the binder — there is no /groups/.../updates/{seq} and no flat
 * /updates/{seq} (a per-binder seq is meaningless without its binder). The
 * binder is keyed by its short URL (the value the feed API and
 * `update.binder_url` use); the trailing segment is the per-binder seq.
 * Returns null for any non-matching path. Query params (e.g. ?page=) are
 * layered on by the caller — this is path-only.
 *
 * @param {string} path - window.location.pathname
 * @returns {{ view: 'updates', params: { group?: string, binder?: string, seq?: string } } | null}
 *          (when present, `seq` is the per-binder seq string)
 */
export function parseUpdatesRoute(path) {
  const groupMatch = path.match(/^\/groups\/([^/]+)\/updates\/?$/);
  if (groupMatch) {
    return { view: 'updates', params: { group: groupMatch[1] } };
  }

  const segs = path.split('/').filter(Boolean);
  const isBinderSeg = (s) => {
    if (!s.includes('.')) return false;
    const [code, ...idParts] = s.split('.');
    return !!PATH_CODE_MAP[code] && idParts.join('.').length > 0;
  };

  // /{short}/updates/{seq} → one Update's detail, nested under its binder
  if (segs.length === 3 && segs[1] === 'updates' && isBinderSeg(segs[0])) {
    return { view: 'updates', params: { binder: `/${segs[0]}`, seq: segs[2] } };
  }

  // /{short}/updates → a binder's feed
  if (segs.length === 2 && segs[1] === 'updates' && isBinderSeg(segs[0])) {
    return { view: 'updates', params: { binder: `/${segs[0]}` } };
  }

  return null;
}
